<template>
  <Content class="goodsBox">
    <Card>
      <div slot="title">
        <span>商品管理</span>
      </div>

      <!-- 库存预警提示 -->
      <Alert v-if="lowStockCount > 0" type="warning" show-icon style="margin-bottom: 16px;">
        <template slot="desc">
          有 <strong>{{ lowStockCount }}</strong> 个商品库存不足（低于预警值），
          <a @click="showLowStockOnly = !showLowStockOnly">
            {{ showLowStockOnly ? '显示全部' : '只看低库存商品' }}
          </a>
        </template>
      </Alert>

      <Form id="searchForm" :model="formItem" :label-width="80" inline>
        <FormItem label="商品名称">
          <Input v-model="formItem.key" placeholder="模糊搜索" clearable style="width: 180px;" />
        </FormItem>
        <FormItem label="分类">
          <Select v-model="formItem.type_id" clearable placeholder="请选择" style="width: 150px;">
            <Option v-for="item in typeList" :key="item.type_id" :value="item.type_id">
              {{ item.name }}
            </Option>
          </Select>
        </FormItem>
        <FormItem label="状态">
          <Select v-model="formItem.status" clearable placeholder="请选择" style="width: 120px;">
            <Option value="">全部</Option>
            <Option value="up">上架</Option>
            <Option value="down">下架</Option>
          </Select>
        </FormItem>
        <FormItem label="库存">
          <Select v-model="formItem.stockFilter" clearable placeholder="请选择" style="width: 130px;">
            <Option value="">全部</Option>
            <Option value="low">库存不足</Option>
            <Option value="out">已售罄</Option>
          </Select>
        </FormItem>
        <FormItem :label-width="20">
          <Button type="primary" @click="search">查询</Button>
          <Button style="margin-left: 8px" @click="clear">重置</Button>
        </FormItem>
        <FormItem class="action-buttons-right">
          <Button type="primary" icon="ios-add" @click="show()">添加商品</Button>
          <Button type="info" icon="ios-download-outline" @click="exportData" style="margin-left: 8px;">导出</Button>
          <Dropdown trigger="click" @on-click="handleBatchAction" style="margin-left: 8px;">
            <Button type="warning" :disabled="selectedGoods.length === 0">
              批量操作 <Icon type="ios-arrow-down"></Icon>
            </Button>
            <DropdownMenu slot="list">
              <DropdownItem name="up">批量上架</DropdownItem>
              <DropdownItem name="down">批量下架</DropdownItem>
              <DropdownItem name="stock">批量调整库存</DropdownItem>
              <DropdownItem name="price">批量调整价格</DropdownItem>
              <DropdownItem name="delete" divided>批量删除</DropdownItem>
            </DropdownMenu>
          </Dropdown>
        </FormItem>
      </Form>

      <Table
        :columns="columns"
        :data="displayData"
        :height="tablesHeight"
        border
        @on-selection-change="handleSelectionChange"
      ></Table>
      <Page
        class="table_page_css"
        v-show="total"
        :total="total"
        :current="searchInfo.pageNo"
        :page-size="searchInfo.pageSize"
        :page-size-opts="pageSizeOpts"
        @on-change="pageNoChange"
        @on-page-size-change="pageSizeChange"
        show-sizer
        show-elevator
        show-total
      />
    </Card>

    <AddUpdate @reload="getList" ref="AddUpdate"></AddUpdate>

    <!-- 批量调整库存弹窗 -->
    <Modal v-model="stockModal" title="批量调整库存" @on-ok="submitBatchStock">
      <Form :model="stockForm" :label-width="100">
        <FormItem label="调整方式">
          <RadioGroup v-model="stockForm.type">
            <Radio label="set">设置为</Radio>
            <Radio label="add">增加</Radio>
            <Radio label="reduce">减少</Radio>
          </RadioGroup>
        </FormItem>
        <FormItem label="库存数量">
          <InputNumber v-model="stockForm.value" :min="0" style="width: 200px;" />
        </FormItem>
      </Form>
      <p style="color: #909399; font-size: 12px;">将对选中的 {{ selectedGoods.length }} 个商品进行库存调整</p>
    </Modal>

    <!-- 批量调整价格弹窗 -->
    <Modal v-model="priceModal" title="批量调整价格" @on-ok="submitBatchPrice">
      <Form :model="priceForm" :label-width="100">
        <FormItem label="调整方式">
          <RadioGroup v-model="priceForm.type">
            <Radio label="percent">按比例调整</Radio>
            <Radio label="fixed">固定金额调整</Radio>
          </RadioGroup>
        </FormItem>
        <FormItem :label="priceForm.type === 'percent' ? '调整比例' : '调整金额'">
          <InputNumber 
            v-model="priceForm.value" 
            :step="priceForm.type === 'percent' ? 1 : 0.01"
            style="width: 200px;" 
          />
          <span style="margin-left: 8px;">
            {{ priceForm.type === 'percent' ? '%' : '元' }}
          </span>
        </FormItem>
        <FormItem label="调整方向">
          <RadioGroup v-model="priceForm.direction">
            <Radio label="up">涨价</Radio>
            <Radio label="down">降价</Radio>
          </RadioGroup>
        </FormItem>
      </Form>
      <p style="color: #909399; font-size: 12px;">将对选中的 {{ selectedGoods.length }} 个商品进行价格调整</p>
    </Modal>
  </Content>
</template>
<script>
import { goodsPage, goodsDel, goodsTypePage, updateGoodsStatus, batchUpdateStatus, batchUpdateStock, batchUpdatePrice, batchDeleteGoods, exportGoods, getLowStockGoods } from '@/api/goods'
import AddUpdate from './GoodsAddUpdate'
export default {
  name: 'Goods',
  components: {
    AddUpdate,
  },
  data() {
    return {
      columns: [
        {
          type: 'selection',
          width: 60,
          align: 'center',
        },
        {
          title: '名称',
          key: 'name',
          width: 140,
          align: 'center',
        },
        {
          title: '价格',
          key: 'price',
          width: 120,
          align: 'center',
          render: (h, params) => {
            const price = params.row.price
            if (!price || price === 0) {
              return h('span', { style: { color: '#909399' } }, '-')
            }
            return h('span', { style: { color: '#f56c6c', fontWeight: 'bold' } }, `¥${parseFloat(price).toFixed(2)}`)
          }
        },
        {
          title: '库存',
          key: 'num',
          width: 120,
          align: 'center',
          render: (h, params) => {
            const num = params.row.num || 0
            const threshold = params.row.stockThreshold || 10
            let color = 'default'
            if (num === 0) color = 'error'
            else if (num <= threshold) color = 'warning'
            return h('Tag', { props: { color } }, num)
          }
        },
        {
          title: '状态',
          key: 'status',
          width: 100,
          align: 'center',
          render: (h, params) => {
            const status = params.row.status || 'up'
            return h('Button', {
              props: { 
                type: status === 'up' ? 'success' : 'default',
                size: 'small'
              },
              style: {
                minWidth: '50px',
                padding: '2px 8px',
                fontSize: '12px',
                lineHeight: '1.5',
                whiteSpace: 'nowrap'
              }
            }, status === 'up' ? '上架' : '下架')
          }
        },
        {
          title: '售卖范围',
          key: 'fulfillment_scope',
          width: 110,
          align: 'center',
          render: (h, params) => {
            const s = String(params.row.fulfillment_scope || 'both').trim()
            const map = {
              both: '堂食+外卖',
              dine_in: '仅堂食',
              delivery: '仅外卖',
            }
            const text = map[s] || map.both
            const color = s === 'dine_in' ? 'blue' : s === 'delivery' ? 'orange' : 'default'
            return h('Tag', { props: { color } }, text)
          },
        },
        {
          title: '描述',
          key: 'desc',
          width: 280,
          align: 'center',
          ellipsis: true,
          tooltip: true,
        },
        {
          title: '类别',
          key: 'typename',
          width: 120,
          align: 'center',
          render(h, params) {
            if (params.row.foodtypes && params.row.foodtypes.length > 0) {
              return h('div', params.row.foodtypes[0].name || '未知分类')
            } else {
              return h('div', { style: { color: '#999' } }, '未分类')
            }
          },
        },
        {
          title: '图片',
          key: 'img',
          width: 100,
          align: 'center',
          render(h, params) {
            return h('img', {
              attrs: {
                src: process.env.VUE_APP_BASEURL + params.row.img,
              },
              style: {
                width: '40px',
                height: '40px',
                margin: '5px 0',
                borderRadius: '4px',
              },
            })
          },
        },
        {
          title: '操作',
          key: 'action',
          width: 200,
          align: 'center',
          render: (h, params) => {
            const status = params.row.status || 'up'
            return h('div', [
              h('Button', {
                props: { 
                  type: status === 'up' ? 'warning' : 'success', 
                  size: 'small' 
                },
                style: { marginRight: '5px' },
                on: { 
                  click: () => this.toggleStatus(params.row) 
                }
              }, status === 'up' ? '下架' : '上架'),
              h('Button', {
                props: { type: 'primary', size: 'small' },
                style: { marginRight: '5px' },
                on: { click: () => this.show(params.row) }
              }, '编辑'),
              h('Poptip', {
                props: { confirm: true, transfer: true, title: '确认删除该商品吗？' },
                on: { 'on-ok': () => this.remove(params.row._id) }
              }, [
                h('Button', { props: { type: 'error', size: 'small' } }, '删除')
              ])
            ])
          },
        },
      ],
      tableData: [],
      total: 0,
      selectedGoods: [],
      lowStockCount: 0,
      showLowStockOnly: false,
      searchInfo: {
        pageNo: 1,
        pageSize: 10,
      },
      pageSizeOpts: [10, 20, 50, 100],
      formItem: {
        key: '',
        type_id: '',
        status: '',
        stockFilter: '',
      },
      typeList: [],
      tablesHeight: 0,
      stockModal: false,
      priceModal: false,
      stockForm: {
        type: 'set',
        value: 0
      },
      priceForm: {
        type: 'percent',
        value: 0,
        direction: 'up'
      }
    }
  },
  computed: {
    displayData() {
      let data = this.tableData
      
      // 处理库存筛选（如果后端不支持，则在前端筛选）
      // 注意：如果后端支持stockFilter参数，这里不应该再次筛选
      // 但为了兼容性，前端也进行筛选以确保功能可用
      if (this.formItem.stockFilter) {
        if (this.formItem.stockFilter === 'low') {
          // 库存不足：库存大于0但小于等于预警值
          data = data.filter(item => {
            const num = item.num || 0
            const threshold = item.stockThreshold || 10
            return num > 0 && num <= threshold
          })
        } else if (this.formItem.stockFilter === 'out') {
          // 已售罄：库存为0
          data = data.filter(item => (item.num || 0) === 0)
        }
      }
      
      // 处理低库存筛选提示（在库存筛选之后）
      if (this.showLowStockOnly) {
        data = data.filter(item => {
          const threshold = item.stockThreshold || 10
          return (item.num || 0) <= threshold
        })
      }
      
      return data
    }
  },
  methods: {
    getTypeList() {
      goodsTypePage({ pageNo: 1, pageSize: 200 })
        .then((res) => {
          if (res.data.code === 200) {
            this.typeList = (res.data.data || []).map((row) => ({
              ...row,
              type_id: row.type_id != null ? row.type_id : row.typeid,
            }))
          } else {
            this.$Message.error(res.data.msg)
          }
        })
        .catch((err) => {
          this.$Message.error(err)
        })
    },
    setTablesHeight() {
      try {
        let aH = 0
        let oA = document.getElementById('app')
        if (!oA) return
        let oH = oA.clientHeight || oA.offsetHeight
        let sA = document.getElementById('searchForm')
        let sH = sA ? (sA.clientHeight || sA.offsetHeight) : 0
        aH = oH - sH - 350
        this.tablesHeight = aH > 0 ? aH : 400
      } catch (error) {
        console.warn('设置表格高度失败:', error)
        this.tablesHeight = 400
      }
    },
    getList() {
      const params = {
        ...this.searchInfo,
        key: this.formItem.key,
        type_id: this.formItem.type_id,
        status: this.formItem.status,
        stockFilter: this.formItem.stockFilter
      }
      
      goodsPage(params)
        .then((res) => {
          if (res.data.code === 200) {
            const data = res.data
            this.tableData = data.data
            // 如果后端支持stockFilter，total应该是筛选后的总数
            // 如果后端不支持，前端筛选会导致total不准确，这里先使用原始total
            this.total = data.total
            this.countLowStock()
          } else {
            this.$Message.error(res.data.msg)
          }
        })
        .catch((err) => {
          this.$Message.error(err)
        })
    },
    countLowStock() {
      this.lowStockCount = this.tableData.filter(item => {
        const threshold = item.stockThreshold || 10
        return item.num <= threshold
      }).length
    },
    search() {
      this.searchInfo.pageNo = 1
      this.showLowStockOnly = false
      this.getList()
    },
    clear() {
      this.formItem = {
        key: '',
        type_id: '',
        status: '',
        stockFilter: '',
      }
      this.searchInfo.pageNo = 1
      this.showLowStockOnly = false
      this.getList()
    },
    show(params) {
      if (params) {
        this.$refs.AddUpdate.init(params)
      } else {
        this.$refs.AddUpdate.init()
      }
    },
    remove(_id) {
      goodsDel({ _id: _id })
        .then((res) => {
          if (res.data.code === 200) {
            this.$Message.success('删除成功')
            this.getList()
          } else {
            this.$Message.error(res.data.msg)
          }
        })
        .catch((err) => {
          this.$Message.error(err)
        })
    },
    toggleStatus(row) {
      const currentStatus = row.status || 'up'
      const newStatus = currentStatus === 'up' ? 'down' : 'up'
      const action = newStatus === 'up' ? '上架' : '下架'
      
      this.$Modal.confirm({
        title: `确认${action}`,
        content: `确定要将商品"${row.name}"${action}吗？`,
        onOk: () => {
          updateGoodsStatus({ _id: row._id, status: newStatus })
            .then((res) => {
              if (res.data.code === 200) {
                this.$Message.success(`${action}成功`)
                this.getList()
              } else {
                this.$Message.error(res.data.msg || `${action}失败`)
              }
            })
            .catch((err) => {
              this.$Message.error(`${action}失败`)
              console.error(err)
            })
        }
        })
    },
    handleSelectionChange(selection) {
      this.selectedGoods = selection.map(item => item._id)
    },
    handleBatchAction(name) {
      if (this.selectedGoods.length === 0) {
        this.$Message.warning('请先选择商品')
        return
      }
      
      switch (name) {
        case 'up':
        case 'down':
          this.batchUpdateStatus(name)
          break
        case 'stock':
          this.stockModal = true
          break
        case 'price':
          this.priceModal = true
          break
        case 'delete':
          this.batchDelete()
          break
      }
    },
    async batchUpdateStatus(status) {
      const action = status === 'up' ? '上架' : '下架'
      
      this.$Modal.confirm({
        title: `确认批量${action}`,
        content: `确定要将选中的 ${this.selectedGoods.length} 个商品${action}吗？`,
        onOk: async () => {
          try {
            const res = await batchUpdateStatus({
              ids: this.selectedGoods,
              status: status
            })
            
            if (res.data && (res.data.success || res.data.code === 200)) {
              this.$Message.success(`批量${action}成功`)
              this.selectedGoods = []
              this.getList()
            } else {
              this.$Message.error(res.data?.message || `批量${action}失败`)
            }
          } catch (error) {
            this.$Message.error(`批量${action}失败`)
          }
        }
      })
    },
    async submitBatchStock() {
      try {
        const res = await batchUpdateStock({
          ids: this.selectedGoods,
          type: this.stockForm.type,
          value: this.stockForm.value
        })
        
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.$Message.success('库存调整成功')
          this.selectedGoods = []
          this.stockModal = false
          this.getList()
        } else {
          this.$Message.error(res.data?.message || '库存调整失败')
        }
      } catch (error) {
        this.$Message.error('库存调整失败')
      }
    },
    async submitBatchPrice() {
      try {
        const res = await batchUpdatePrice({
          ids: this.selectedGoods,
          type: this.priceForm.type,
          value: this.priceForm.value,
          direction: this.priceForm.direction
        })
        
        if (res.data && (res.data.success || res.data.code === 200)) {
          this.$Message.success('价格调整成功')
          this.selectedGoods = []
          this.priceModal = false
          this.getList()
        } else {
          this.$Message.error(res.data?.message || '价格调整失败')
        }
      } catch (error) {
        this.$Message.error('价格调整失败')
      }
    },
    batchDelete() {
      this.$Modal.confirm({
        title: '确认批量删除',
        content: `确定要删除选中的 ${this.selectedGoods.length} 个商品吗？此操作不可恢复！`,
        onOk: async () => {
          try {
            const res = await batchDeleteGoods(this.selectedGoods)
            
            if (res.data && (res.data.success || res.data.code === 200)) {
              this.$Message.success('批量删除成功')
              this.selectedGoods = []
              this.getList()
            } else {
              this.$Message.error(res.data?.message || '批量删除失败')
            }
          } catch (error) {
            this.$Message.error('批量删除失败')
          }
        }
      })
    },
    async exportData() {
      try {
        this.$Message.loading({
          content: '正在导出...',
          duration: 0
        })
        
        const res = await exportGoods({
          ...this.formItem,
          format: 'excel'
        })
        
        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `商品列表_${new Date().toISOString().slice(0, 10)}.xlsx`
        link.click()
        window.URL.revokeObjectURL(url)
        
        this.$Message.destroy()
        this.$Message.success('导出成功')
      } catch (error) {
        this.$Message.destroy()
        this.$Message.error('导出失败')
      }
    },
    pageNoChange(current) {
      this.searchInfo.pageNo = current
      this.getList()
    },
    pageSizeChange(val) {
      this.searchInfo.pageNo = 1
      this.searchInfo.pageSize = val
      this.getList()
    },
  },
  mounted() {
    this.getTypeList()
    this.getList()
    setTimeout(() => {
      this.setTablesHeight()
    }, 50)
    window.onresize = () => {
      this.setTablesHeight()
    }
  },
}
</script>
<style lang="less" scoped>
.goodsBox {
  .ivu-form-inline .ivu-form-item {
    margin-right: 8px;
    margin-bottom: 16px;
    vertical-align: top;
    
    // 根据内容自适应宽度，不再固定267px
    .ivu-form-item-label {
      text-align: right;
    }
  }
  
  // 确保搜索表单可以正常换行和对齐
  #searchForm {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    margin-bottom: 20px;
    
    .ivu-form-item {
      display: inline-block;
      margin-bottom: 0;
      
      // 右侧操作按钮靠右对齐
      &.action-buttons-right {
        margin-left: auto;
        margin-right: 0;
      }
    }
  }
}
</style>
